import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { meApi, spaceApi } from '../services/api';
import { useSpaceId } from '../contexts/SpaceContext';
import type { Skill, CreateSkillInput, UpdateSkillInput } from '../types';

// ---------------------------------------------------------------------------
// Space-scoped (new) — used by every console skills page
// ---------------------------------------------------------------------------

/** Stable per-space api factory; identity changes only when spaceId changes. */
function useSkillApi() {
  const spaceId = useSpaceId();
  return useMemo(() => spaceApi(spaceId).skills, [spaceId]);
}

export function useSpaceSkills() {
  const spaceId = useSpaceId();
  const api = useSkillApi();
  return useQuery({
    queryKey: ['space', spaceId, 'skills'],
    queryFn: () => api.list(),
  });
}

export function useSpaceSkill(skillId: string | undefined) {
  const spaceId = useSpaceId();
  const api = useSkillApi();
  return useQuery({
    queryKey: ['space', spaceId, 'skills', skillId],
    queryFn: () => api.get(skillId!),
    enabled: !!skillId,
  });
}

export function useCreateSpaceSkill() {
  const spaceId = useSpaceId();
  const api = useSkillApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateSkillInput) => api.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'skills'] });
    },
  });
}

export function useUpdateSpaceSkill() {
  const spaceId = useSpaceId();
  const api = useSkillApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ skillId, input }: { skillId: string; input: UpdateSkillInput }) =>
      api.update(skillId, input),
    onSuccess: (data: Skill) => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'skills'] });
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'skills', data.skillId] });
    },
  });
}

export function useDeleteSpaceSkill() {
  const spaceId = useSpaceId();
  const api = useSkillApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (skillId: string) => api.delete(skillId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['space', spaceId, 'skills'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Cross-space — used by Builder/operator surfaces that need skills from every
// space the user can access.
// ---------------------------------------------------------------------------

export function useMeSkills() {
  return useQuery({
    queryKey: ['me', 'skills'],
    queryFn: () => meApi.skills(),
  });
}

// ---------------------------------------------------------------------------
// Default exports — space-scoped is the right default for most callers.
// ---------------------------------------------------------------------------

export const useSkills = useSpaceSkills;
export const useSkill = useSpaceSkill;
export const useCreateSkill = useCreateSpaceSkill;
export const useUpdateSkill = useUpdateSpaceSkill;
export const useDeleteSkill = useDeleteSpaceSkill;
